// ✅ src/pages/VehicleV2/ProgressMasterPrint.jsx
// React Print Page for OLD ASPX: ProgressMasterPrint.aspx (CrystalReport)
// ✅ Uses SAME PRINT DESIGN TEMPLATE you shared (Header box + title box + bordered table + footer)
// ✅ Reads filters from:
//   1) sessionStorage (ASPX-like): pmtype, pmbranch, pmvehregno, pmvehname, pmvehcode, pmyear, pmmonth
//   2) navigation state: { filters: {...} }
//   3) querystring: ?pmtype=O&pmbranch=8&pmvehcode=... etc
//
// Backend expectation (recommended):
//   GET  /api/progressMaster/print?vehtype=O&branchid=8&vehregno=...&vehname=...&vehcode=...&year=2024&month=January
//   -> { success:true, rows:[...] }
//
// OPTIONAL (mail / pdf):
//   POST /api/progressMaster/exportPdf  (payload same as print params) -> { success:true, fileName:"PROGRESS REPORT....pdf" }
//   then open: /Mail/mail.aspx?Rpt=<fileName>   (if you still have old mail page)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/progressMaster";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// normalize DB row -> UI columns
function normalizeProgressRow(r) {
  return {
    month: pick(r, ["Month", "month", "fld_month"]),
    year: pick(r, ["Year", "year", "fld_year"]),
    branch: pick(r, ["Branch", "branch", "BranchName", "fld_branchname", "fld_BranchName"]),
    kmMonth: pick(r, ["KM_Month", "kmMonth", "KMMonth", "KMOnMonth", "fld_km_month"]),
    hrMonth: pick(r, ["HR_Month", "hrMonth", "HRMonth", "HROnMonth", "fld_hr_month"]),
    productionQty: pick(r, ["ProductionQty", "productionQty", "Production_Qty", "fld_productionqty"]),
    totalHsdIssued: pick(r, ["FuelIssued", "fuelIssued", "TotalHSDIssued", "HSDIssued", "fld_hsdissued"]),
    hsdConsHr: pick(r, ["HSDConsumptionHR", "hsdConsumptionHR", "HSD_Consumption_HR", "fld_hsdcons_hr"]),
    hsdConsKm: pick(r, ["HSDConsumptionKM", "hsdConsumptionKM", "HSD_Consumption_KM", "fld_hsdcons_km"]),
  };
}

export default function ProgressMasterPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [qs] = useSearchParams();

  /* ---------------- read params (sessionStorage first like ASPX) ---------------- */
  const filters = useMemo(() => {
    const fromState = location?.state?.filters || {};

    // sessionStorage (ASPX-style set in ProgressMaster page)
    const ss = {
      pmtype: sessionStorage.getItem("pmtype"),
      pmbranch: sessionStorage.getItem("pmbranch"),
      pmvehregno: sessionStorage.getItem("pmvehregno"),
      pmvehname: sessionStorage.getItem("pmvehname"),
      pmvehcode: sessionStorage.getItem("pmvehcode"),
      pmyear: sessionStorage.getItem("pmyear"),
      pmmonth: sessionStorage.getItem("pmmonth"),
    };

    // querystring fallback
    const q = {
      pmtype: qs.get("pmtype"),
      pmbranch: qs.get("pmbranch"),
      pmvehregno: qs.get("pmvehregno"),
      pmvehname: qs.get("pmvehname"),
      pmvehcode: qs.get("pmvehcode"),
      pmyear: qs.get("pmyear"),
      pmmonth: qs.get("pmmonth"),
    };

    // final merge: state -> ss -> qs
    const merged = {
      pmtype: toStr(fromState.pmtype || ss.pmtype || q.pmtype),
      pmbranch: toStr(fromState.pmbranch || ss.pmbranch || q.pmbranch),
      pmvehregno: toStr(fromState.pmvehregno || ss.pmvehregno || q.pmvehregno),
      pmvehname: toStr(fromState.pmvehname || ss.pmvehname || q.pmvehname),
      pmvehcode: toStr(fromState.pmvehcode || ss.pmvehcode || q.pmvehcode),
      pmyear: toStr(fromState.pmyear || ss.pmyear || q.pmyear),
      pmmonth: toStr(fromState.pmmonth || ss.pmmonth || q.pmmonth),
    };

    return merged;
  }, [location?.state, qs]);

  /* ---------------- data ---------------- */
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        // if nothing available, show error
        if (!filters.pmtype && !filters.pmbranch && !filters.pmvehcode) {
          throw new Error("Print filters missing (pmtype/pmbranch/pmvehcode). Open print from Progress Master page.");
        }

        // ✅ recommended backend route
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            vehtype: filters.pmtype,
            branchid: filters.pmbranch,
            vehregno: filters.pmvehregno,
            vehname: filters.pmvehname,
            vehcode: filters.pmvehcode,
            year: filters.pmyear,
            month: filters.pmmonth,
          },
        });

        const list = Array.isArray(data) ? data : data?.rows || data?.data || [];
        if (!Array.isArray(list)) throw new Error("Invalid print response");

        if (alive) setRows(list);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [filters]);

  const normRows = useMemo(() => rows.map(normalizeProgressRow), [rows]);

  /* OPTIONAL: mail button (only works if backend supports exportPdf + you still have mail.aspx) */
  const onMail = async () => {
    try {
      // comment this if you don't have backend export endpoint
      const { data } = await axios.post(`${API_BASE}/exportPdf`, {
        vehtype: filters.pmtype,
        branchid: filters.pmbranch,
        vehregno: filters.pmvehregno,
        vehname: filters.pmvehname,
        vehcode: filters.pmvehcode,
        year: filters.pmyear,
        month: filters.pmmonth,
      });

      const fileName = data?.fileName;
      if (!fileName) throw new Error("PDF export failed (fileName missing)");

      window.open(`/Mail/mail.aspx?Rpt=${encodeURIComponent(fileName)}`, "_blank", "width=650,height=280");
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Mail/PDF export not configured");
    }
  };

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>

          <div className="flex items-center gap-2">
            {/* Optional mail (like ASPX imgMail) */}
            <button
              onClick_toggle="noop"
              onClick={onMail}
              className="px-4 py-2 border rounded hover:bg-gray-50"
              title="Send mail (requires exportPdf backend + old mail.aspx)"
            >
              ✉️ Mail
            </button>

            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* Header (same template you shared) */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Progress Master Report</h2>
        </div>

        {/* Filter line (nice to have like Crystal params) */}
        <div className="mb-2 border border-black p-1 text-xs">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <div>
              <b>Type:</b> {toStr(filters.pmtype || "-")}
            </div>
            <div>
              <b>Branch:</b> {toStr(filters.pmbranch || "-")}
            </div>
            <div>
              <b>Veh Code:</b> {toStr(filters.pmvehcode || "-")}
            </div>
            <div>
              <b>Veh Name:</b> {toStr(filters.pmvehname || "-")}
            </div>
            <div>
              <b>Reg No:</b> {toStr(filters.pmvehregno || "-")}
            </div>
            <div>
              <b>Month/Year:</b> {toStr(filters.pmmonth || "-")} / {toStr(filters.pmyear || "-")}
            </div>
          </div>
        </div>

        {/* Table (Progress columns) */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Month</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Year</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Branch</th>
                <th className="border border-black px-1 py-1 font-bold text-center">KM on Month</th>
                <th className="border border-black px-1 py-1 font-bold text-center">HR on Month</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Production Qty</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Total HSD Issued</th>
                <th className="border border-black px-1 py-1 font-bold text-center">HSD Consumption HR</th>
                <th className="border border-black px-1 py-1 font-bold text-center">HSD Consumption KM</th>
              </tr>
            </thead>

            <tbody>
              {!loading && normRows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="border border-black px-2 py-2 text-center">
                    No data to print.
                  </td>
                </tr>
              ) : (
                normRows.map((r, i) => (
                  <tr key={i} className="bg-white">
                    <td className="border border-black px-1 py-1 text-center">{i + 1}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.month}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.year}</td>
                    <td className="border border-black px-1 py-1">{r.branch}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.kmMonth}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.hrMonth}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.productionQty}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.totalHsdIssued}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.hsdConsHr}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.hsdConsKm}</td>
                  </tr>
                ))
              )}

              {/* Totals row (optional) */}
              {normRows.length > 0 && (
                <tr className="bg-gray-100 font-bold">
                  <td className="border border-black px-1 py-1 text-center" colSpan={4}>
                    TOTAL
                  </td>
                  <td className="border border-black px-1 py-1 text-right">
                    {normRows.reduce((a, x) => a + num(x.kmMonth), 0)}
                  </td>
                  <td className="border border-black px-1 py-1 text-right">
                    {normRows.reduce((a, x) => a + num(x.hrMonth), 0)}
                  </td>
                  <td className="border border-black px-1 py-1 text-right">
                    {normRows.reduce((a, x) => a + num(x.productionQty), 0)}
                  </td>
                  <td className="border border-black px-1 py-1 text-right">
                    {normRows.reduce((a, x) => a + num(x.totalHsdIssued), 0)}
                  </td>
                  <td className="border border-black px-1 py-1 text-right">
                    {normRows.reduce((a, x) => a + num(x.hsdConsHr), 0)}
                  </td>
                  <td className="border border-black px-1 py-1 text-right">
                    {normRows.reduce((a, x) => a + num(x.hsdConsKm), 0)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>
      </div>
    </div>
  );
}
